import uniqBy from "lodash/uniqBy";
import { useCallback, useEffect, useMemo, useState } from "react";
import { MapRef, useMap } from "react-map-gl/maplibre";
import { DownloadFormat } from "~mangomap-ui/components/core/tool-panel/tools/query-tool/components/download-menu";
import { QueryTool as View } from "~mangomap-ui/components/core/tool-panel/tools/query-tool/query-tool";
import {
	QueryConfig,
	QueryErrorState,
	QueryField,
	QueryResults,
} from "~mangomap-ui/components/core/tool-panel/tools/query-tool/types";
import { getLineSimpleStyles } from "~map-style-utils/get-line-style/simple-style";
import { getPointSimpleStyles } from "~map-style-utils/get-point-style/simple-style";
import { getPolygonSimpleStyles } from "~map-style-utils/get-polygon-style/simple-style";
import {
	getDatasetSchemaQueryFn,
	GetDatasetSchemaResults,
} from "~map-tools/queries/use-get-dataset-schema";
import { useGetMapQueryConfigurationByMap } from "~map-tools/queries/use-get-map-query-configuration-by-map";
import { useSpatialSelection } from "~map-view/hooks/use-spatial-selection";
import { useGenerateSpatialQueryDownloadLink } from "~map-view/queries/use-generate-spatial-query-download-link";
import { useSpatialQueryGids } from "~map-view/queries/use-spatial-query-gids";
import { useSpatialQueryProperties } from "~map-view/queries/use-spatial-query-properties";
import { useSpatialQueryReports } from "~map-view/queries/use-spatial-query-reports";
import { useGetCurrentMap } from "~map-view/queries/useGetCurrentMap";
import { useCloseMapTool, useStore } from "~map-view/states";
import {
	ATTRIBUTE_FILTER_TYPE,
	GEOMETRY_TYPE,
	OUTPUT_FORMAT,
	PROPERTY_OPERATOR,
	PropertyFilter,
	SPATIAL_FILTER_TYPE,
	SPATIAL_RELATION,
	SpatialQueryInput,
} from "~map-view/types/__generated/gql/graphql";
import { toast } from "~ui-components/components/atoms/toaster";
import { useWebRouter } from "~utils/next-router/use-web-router";

export const QueryTool = () => {
	const { default: maplibreMap } = useMap();

	const router = useWebRouter();
	const setCurrentSpatialType = useStore(
		(state) => state.setCurrentSpatialType,
	);
	const currentSpatialGeometry = useStore(
		(state) => state.currentSpatialGeometry,
	);
	const currentSpatialType = useStore((state) => state.currentSpatialType);
	const setCurrentSpatialGeometry = useStore(
		(state) => state.setCurrentSpatialGeometry,
	);
	const closeMapTool = useCloseMapTool();
	const { data: map } = useGetCurrentMap();
	const { data: mapQueryConfig } = useGetMapQueryConfigurationByMap({
		mapId: router._query.mapId ?? "",
	});
	const [datasetIdToSchemaMap, setDatasetIdToSchemaMap] = useState<
		Record<string, GetDatasetSchemaResults>
	>({});

	const [results, setResults] = useState<QueryResults>();
	const [queryError, setQueryError] = useState<QueryErrorState>();
	const [query, setQuery] = useState<QueryConfig | null>(null);
	const [selectedGids, setSelectedGids] = useState<number[]>([]);
	const [spatialQueryInput, setSpatialQueryInput] =
		useState<SpatialQueryInput | null>(null);
	const [page, setPage] = useState<number>(0);
	const [resultsBbox, setResultsBbox] = useState<
		[number, number, number, number] | null
	>(null);
	const { refetch: refetchSpatialQueryGids } = useSpatialQueryGids({
		input: spatialQueryInput!,
	});
	const { refetch: refetchSpatialQueryReports } = useSpatialQueryReports({
		mapId: map?.id ?? "",
		queryId: query?.id ?? "",
		input: spatialQueryInput!,
	});
	const {
		data: spatialQueryPropertiesResults,
		refetch: refetchSpatialQueryProperties,
	} = useSpatialQueryProperties({
		input: spatialQueryInput!,
		limit: 20,
		offset: page * 20,
	});
	const generateSpatialQueryDownloadLink =
		useGenerateSpatialQueryDownloadLink();

	const datasetIdToSourceDatasetIdMap = useMemo<Record<string, string>>(() => {
		if (!mapQueryConfig || !map) return {};

		const datasetIds = [
			...new Set(
				mapQueryConfig.layerConfigurations.map(
					(config) => config._layer.datasetId,
				),
			),
		];

		const datasets = map.layerGroupCollection.items
			.map((layerGroup) =>
				layerGroup.layerCollection.items.map((layer) => layer._dataset),
			)
			.flat();

		const sourceDatasetIds = datasetIds.reduce(
			(acc, id) => {
				const sourceDatasetId = datasets.find(
					(d) => d.id === id,
				)?.sourceDatasetId;
				if (sourceDatasetId) {
					acc[id] = sourceDatasetId;
				}
				return acc;
			},
			{} as Record<string, string>,
		);

		return sourceDatasetIds;
	}, [map, mapQueryConfig]);

	const pointModeValidation = useCallback(
		(features) => {
			if (!maplibreMap || !query || !mapQueryConfig) return false;

			if (
				features.properties.mode === "point" &&
				features.geometry.type === "Point"
			) {
				const coordinates = features.geometry.coordinates;
				const point = maplibreMap
					.getMap()
					.project([coordinates[0]!, coordinates[1]!]);
				const queriedFeatures = maplibreMap
					.getMap()
					.queryRenderedFeatures([point.x, point.y]);

				const currentConfig = mapQueryConfig.layerConfigurations.find(
					(config) => config.id === query.id,
				);
				if (!currentConfig) return false;

				const matchedFeatures = uniqBy(
					queriedFeatures.filter(
						(feature) => feature.source === currentConfig._layer.datasetId,
					),
					(feature) => feature.id,
				);

				const sourceDatasetId =
					datasetIdToSourceDatasetIdMap[currentConfig._layer.datasetId];

				if (!sourceDatasetId) return false;

				setSelectedGids((prevIds) => {
					const clickedIds = matchedFeatures.map(
						(feature) => feature.properties.gid,
					);

					// Toggle selection: add new IDs or remove if already selected
					const newIds = [...prevIds];

					for (const clickedId of clickedIds) {
						const existingIndex = newIds.indexOf(clickedId);
						if (existingIndex >= 0) {
							newIds.splice(existingIndex, 1);
						} else {
							newIds.push(clickedId);
						}
					}

					addSelectedFeaturesToMap(
						maplibreMap,
						newIds,
						currentConfig._layer.datasetId,
						`public.${sourceDatasetId}`,
						currentConfig.layerId,
						currentConfig._layer._dataset.type,
					);

					return newIds;
				});
			}

			return false;
		},
		[maplibreMap, query, mapQueryConfig, datasetIdToSourceDatasetIdMap],
	);

	const handleRowClick = (row: any) => {
		if (!maplibreMap || !query || !mapQueryConfig) return;

		const currentConfig = mapQueryConfig.layerConfigurations.find(
			(config) => config.id === query.id,
		);
		if (!currentConfig) return;

		if (row === null) {
			clearHighlightedFeature(maplibreMap);
			if (resultsBbox?.length === 4) {
				maplibreMap.fitBounds(
					[
						[resultsBbox[0], resultsBbox[1]], // southwest
						[resultsBbox[2], resultsBbox[3]], // northeast
					],
					{
						padding: {
							top: 24,
							right: 24,
							bottom: 24,
							left: 398 + 24, // 398 for drawer width
						},
						duration: 1000,
					},
				);
			}
			return;
		}

		// Find the feature on the map to get its geometry
		const sourceDatasetId =
			datasetIdToSourceDatasetIdMap[currentConfig._layer.datasetId];
		if (!sourceDatasetId) return;

		// Add highlighting for this feature
		addHighlightedFeatureToMap(
			maplibreMap,
			row.gid,
			currentConfig._layer.datasetId,
			`public.${sourceDatasetId}`,
			currentConfig.layerId,
			currentConfig._layer._dataset.type,
			mapQueryConfig.color,
		);

		if (row.point) {
			maplibreMap.flyTo({
				center: row.point,
				zoom: Math.max(maplibreMap.getZoom() + 2, 16),
				duration: 1000,
			});
		}

		if (row.bbox) {
			maplibreMap.fitBounds(
				[
					[row.bbox[0], row.bbox[1]], // southwest
					[row.bbox[2], row.bbox[3]], // northeast
				],
				{
					padding: {
						top: 24,
						right: 24,
						bottom: 24,
						left: 398 + 24, // 398 for drawer width
					},
					duration: 1000,
				},
			);
		}
	};

	useSpatialSelection({
		map: maplibreMap,
		currentMode: currentSpatialType
			? SPATIAL_TYPE_TO_DRAW_MODE_MAP[currentSpatialType]
			: "select",
		onFinish: setCurrentSpatialGeometry,
		pointModeValidation,
	});

	useEffect(() => {
		return () => {
			setCurrentSpatialType(null);
			setCurrentSpatialGeometry(null);
		};

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		async function fetchDatasetSchemas() {
			const schemas = await Promise.all(
				Object.keys(datasetIdToSourceDatasetIdMap).map((datasetId) =>
					getDatasetSchemaQueryFn({
						tableName: datasetIdToSourceDatasetIdMap[datasetId]!,
					}).then((schema) => ({
						[datasetId]: schema,
					})),
				),
			);

			setDatasetIdToSchemaMap(
				schemas.reduce((acc, schema) => ({ ...acc, ...schema }), {}),
			);
		}

		void fetchDatasetSchemas();
	}, [datasetIdToSourceDatasetIdMap]);

	const queries: QueryConfig[] = useMemo(() => {
		if (!mapQueryConfig) return [];

		return mapQueryConfig.layerConfigurations.map(
			(config) =>
				({
					id: config.id,
					name: config.title,
					displayName: config.title,
					description: config.description ?? "",
					enabled: true,
					schema: {
						id: config.id,
						spatialSelection: {
							enabled: config.spatialFilters?.enabled ?? false,
							tools:
								(config.spatialFilters?.allowedTypes?.map((type) =>
									type.toLowerCase(),
								) as NonNullable<
									NonNullable<QueryConfig["schema"]>["spatialSelection"]
								>["tools"]) ?? [],
						},
						allowedDownloadFormats:
							config.outputSettings.allowedFormats.map<DownloadFormat>(
								(format) => {
									if (format === OUTPUT_FORMAT.CSV) return "csv";
									if (format === OUTPUT_FORMAT.KML) return "kml";
									if (format === OUTPUT_FORMAT.SHAPEFILE) return "shp";

									throw new Error(`Unsupported format: ${format}`);
								},
							),
						fields: config.attributeFilters.map(
							(field) =>
								({
									id: field.propertyName,
									name: field.propertyName,
									displayName: field.propertyName,
									type: FIELD_TYPE_MAP[field.filterType],
									dataType: DATA_TYPE_MAP[field.filterType],
									options:
										datasetIdToSchemaMap[config._layer.datasetId]?.columns.find(
											(column) => column.name === field.propertyName,
										)?.sampleValues ?? [],
									min: +(
										datasetIdToSchemaMap[config._layer.datasetId]?.columns.find(
											(column) => column.name === field.propertyName,
										)?.minValue ?? 0
									),
									max: +(
										datasetIdToSchemaMap[config._layer.datasetId]?.columns.find(
											(column) => column.name === field.propertyName,
										)?.maxValue ?? 0
									),
									minDate:
										datasetIdToSchemaMap[config._layer.datasetId]?.columns.find(
											(column) => column.name === field.propertyName,
										)?.minValue ?? undefined,
									maxDate:
										datasetIdToSchemaMap[config._layer.datasetId]?.columns.find(
											(column) => column.name === field.propertyName,
										)?.maxValue ?? undefined,
								}) satisfies QueryField,
						),
					},
				}) satisfies QueryConfig,
		);
	}, [datasetIdToSchemaMap, mapQueryConfig]);

	return (
		<View
			title="Query"
			excludeColumns={["gid", "bbox", "point"]}
			onClose={() => {
				setQueryError(undefined);
				setResults(undefined);
				setPage(0);
				setSelectedGids([]);
				setSpatialQueryInput(null);
				setCurrentSpatialType(null);
				setCurrentSpatialGeometry(null);
				setResultsBbox(null);
				if (maplibreMap) {
					clearQueryResults(maplibreMap);
					clearSelectedFeatures(maplibreMap);
					clearHighlightedFeature(maplibreMap);
				}
				closeMapTool();
			}}
			onSecondaryAction={() => {
				setQueryError(undefined);
				setResults(undefined);
				setPage(0);
				setSelectedGids([]);
				setSpatialQueryInput(null);
				setCurrentSpatialType(null);
				setCurrentSpatialGeometry(null);
				setResultsBbox(null);
				if (maplibreMap) {
					clearQueryResults(maplibreMap);
					clearSelectedFeatures(maplibreMap);
					clearHighlightedFeature(maplibreMap);
				}
			}}
			queries={queries}
			results={results}
			queryError={queryError}
			page={page}
			totalPages={Math.ceil((spatialQueryPropertiesResults?.total ?? 0) / 20)}
			onPageChange={(newPage) => {
				setPage(newPage);
				// wait a bit for the spatialQueryInput to be updated
				void new Promise((resolve) => setTimeout(resolve, 50)).then(() => {
					refetchSpatialQueryProperties()
						.then(({ data: updatedSpatialQueryPropertiesResults }) => {
							setResults(
								(prev) =>
									({
										...prev,
										resultCount: updatedSpatialQueryPropertiesResults!.total,
										data: updatedSpatialQueryPropertiesResults!.items.map(
											(item) => ({
												...JSON.parse(item.properties),
												gid: item.gid,
												bbox: item.bbox,
												point: item.point,
											}),
										),
									}) as QueryResults,
							);
						})
						.catch(console.error);
				});
			}}
			onSpatialSelectionChange={(type) => {
				setCurrentSpatialType(
					(type ? type.toUpperCase() : type) as SPATIAL_FILTER_TYPE | null,
				);
				setSelectedGids([]);
				if (maplibreMap) {
					clearSelectedFeatures(maplibreMap);
				}
			}}
			onQuerySelect={(query) => {
				setQueryError(undefined);
				setResults(undefined);
				setPage(0);
				setQuery(query);
				setSelectedGids([]);
				setSpatialQueryInput(null);
				setCurrentSpatialType(null);
				setCurrentSpatialGeometry(null);
				setResultsBbox(null);
				if (maplibreMap) {
					clearQueryResults(maplibreMap);
					clearSelectedFeatures(maplibreMap);
					clearHighlightedFeature(maplibreMap);
				}
			}}
			onClear={() => {
				setQueryError(undefined);
				setResults(undefined);
				setPage(0);
				setSelectedGids([]);
				setSpatialQueryInput(null);
				setCurrentSpatialType(null);
				setCurrentSpatialGeometry(null);
				setResultsBbox(null);
				if (maplibreMap) {
					clearQueryResults(maplibreMap);
					clearSelectedFeatures(maplibreMap);
					clearHighlightedFeature(maplibreMap);
				}
			}}
			onDownload={(format) => {
				if (!spatialQueryInput) return;

				const formatDict = {
					csv: OUTPUT_FORMAT.CSV,
					kml: OUTPUT_FORMAT.KML,
					shp: OUTPUT_FORMAT.SHAPEFILE,
				};

				toast.promise(
					async () => {
						const url = await generateSpatialQueryDownloadLink.mutateAsync({
							format: formatDict[format],
							input: spatialQueryInput,
						});
						window.open(url, "_blank");
					},
					{
						loading: "Processing your request...",
						success: "Success!",
						error: (e) => e.message ?? "Something went wrong!",
					},
				);
			}}
			onRowClick={handleRowClick}
			onSubmit={async (state) => {
				const { selectedQuery, filterValues, spatialSelection } = state;

				try {
					setQueryError(undefined);
					setResults(undefined);
					setPage(0);
					setResultsBbox(null);
					if (maplibreMap) {
						clearHighlightedFeature(maplibreMap);
					}

					if (!selectedQuery) throw new Error("No query selected");

					const { schema, id } = selectedQuery;
					if (!schema) throw new Error("No schema found");

					const layerConfig = mapQueryConfig!.layerConfigurations.find(
						(config) => config.id === id,
					);
					if (!layerConfig) throw new Error("No layer config found");

					const sourceDatasetId =
						datasetIdToSourceDatasetIdMap[layerConfig._layer.datasetId];
					if (!sourceDatasetId) throw new Error("No source dataset id found");

					const properties: PropertyFilter[] = Object.keys(filterValues)
						.map((key) => {
							const matchedSchema = schema.fields.find(
								(field) => field.id === key,
							);
							if (!matchedSchema) return null;

							if (
								matchedSchema.type === "numeric" ||
								matchedSchema.type === "date"
							) {
								return [
									{
										field: key,
										operator: PROPERTY_OPERATOR.GTE,
										value: JSON.stringify(filterValues[key][0]),
									},
									{
										field: key,
										operator: PROPERTY_OPERATOR.LTE,
										value: JSON.stringify(filterValues[key][1]),
									},
								] satisfies PropertyFilter[];
							}

							if (matchedSchema.type === "text" && filterValues[key]?.length) {
								return {
									field: key,
									operator: PROPERTY_OPERATOR.IN,
									value: JSON.stringify(filterValues[key]),
								} satisfies PropertyFilter;
							}

							return null;
						})
						.flat()
						.filter((val) => val !== null);

					const variables: SpatialQueryInput = {
						datasetId: sourceDatasetId,
						geometry: currentSpatialGeometry
							? JSON.stringify(currentSpatialGeometry)
							: undefined,
						relation: SPATIAL_RELATION.WITHIN,
						distanceUnit: layerConfig.measurementUnit,
						properties,
						gids: selectedGids,
					};
					setSpatialQueryInput(variables);

					const startTime = Date.now();

					// wait a bit for the spatialQueryInput to be updated
					await new Promise((resolve) => setTimeout(resolve, 50));

					const [
						{ data: spatialQueryGidsResults },
						{ data: spatialQueryPropertiesResults },
						{ data: spatialQueryReportsResults },
					] = await Promise.all([
						refetchSpatialQueryGids(),
						refetchSpatialQueryProperties(),
						refetchSpatialQueryReports(),
					]);

					if (
						!spatialQueryGidsResults ||
						!spatialQueryPropertiesResults ||
						!spatialQueryReportsResults
					) {
						throw new Error("Failed to fetch spatial query properties");
					}

					const results: QueryResults = {
						id: `query-${startTime}`,
						queryConfig: selectedQuery,
						resultCount: spatialQueryPropertiesResults.total,
						data: spatialQueryPropertiesResults.items.map((item) => ({
							...JSON.parse(item.properties),
							gid: item.gid,
							bbox: item.bbox,
							point: item.point,
						})),
						report: {
							enabled: layerConfig.isReportEnabled,
							summary: "REPORTS SUMMARY PLACEHOLDER",
							content: spatialQueryReportsResults.content ?? "",
						},
						metadata: {
							executionTime: Math.max(
								spatialQueryGidsResults.processTime ?? 0,
								spatialQueryPropertiesResults.processTime ?? 0,
								spatialQueryReportsResults.processTime ?? 0,
							),
							timestamp: new Date(startTime).toISOString(),
							spatialExtent: spatialQueryGidsResults.bbox
								? {
										minX: spatialQueryGidsResults.bbox[0]!,
										minY: spatialQueryGidsResults.bbox[1]!,
										maxX: spatialQueryGidsResults.bbox[2]!,
										maxY: spatialQueryGidsResults.bbox[3]!,
									}
								: undefined,
						},
						status: "success",
					};

					if (maplibreMap) {
						addQueryResultsToMap(
							maplibreMap,
							spatialQueryGidsResults.gids,
							mapQueryConfig!.color,
							layerConfig._layer.datasetId,
							`public.${sourceDatasetId}`,
							layerConfig.layerId,
							layerConfig._layer._dataset.type,
						);

						// Store the results bbox for row click handling
						if (spatialQueryGidsResults.bbox) {
							setResultsBbox(
								spatialQueryGidsResults.bbox as [
									number,
									number,
									number,
									number,
								],
							);

							// Zoom to the spatial extent of the query results
							maplibreMap.fitBounds(
								[
									[
										spatialQueryGidsResults.bbox[0]!,
										spatialQueryGidsResults.bbox[1]!,
									], // southwest
									[
										spatialQueryGidsResults.bbox[2]!,
										spatialQueryGidsResults.bbox[3]!,
									], // northeast
								],
								{
									padding: {
										top: 24,
										right: 24,
										bottom: 24,
										left: 398 + 24, // 398 for drawer width
									},
									duration: 1000,
								},
							);
						}
					}

					if (!spatialQueryGidsResults.gids.length) {
						const errorState: QueryErrorState = {
							type: "no_results",
							message: "No results found for your current search criteria.",
							appliedFilters: filterValues,
							spatialSelection: spatialSelection ?? undefined,
							suggestions: [
								"Try expanding your search area",
								"Adjust filter criteria to be less restrictive",
								"Check if the selected layer contains data for this region",
							],
						};
						setQueryError(errorState);
					} else {
						setResults(results);
					}
				} catch (e) {
					console.error(e);
					setQueryError({
						type: "query_failed",
						message: e instanceof Error ? e.message : "Something went wrong!",
						appliedFilters: filterValues,
						spatialSelection: spatialSelection ?? undefined,
						suggestions: [
							"Check your network connection",
							"Verify the selected layer is available",
							"Try again in a few moments",
						],
					});
				}
			}}
		/>
	);
};

// https://github.com/JamesLMilner/terra-draw/blob/main/packages/terra-draw/src/util/styling.ts#L3
// {
// 	polygonFillColor: "#3f97e0",
// 	polygonOutlineColor: "#3f97e0",
// 	polygonOutlineWidth: 4,
// 	polygonFillOpacity: 0.3,
// 	pointColor: "#3f97e0",
// 	pointOutlineColor: "#ffffff",
// 	pointOutlineWidth: 0,
// 	pointWidth: 6,
// 	lineStringColor: "#3f97e0",
// 	lineStringWidth: 4,
// 	zIndex: 0,
// };

const addSelectedFeaturesToMap = (
	map: MapRef,
	gids: number[],
	sourceId: string,
	sourceLayer: string,
	layerId: string,
	geometryType: GEOMETRY_TYPE,
) => {
	clearSelectedFeatures(map);

	const mapInstance = map.getMap();

	const polygonConfig = {
		symbolStyle: {
			fill: {
				style: "solid",
				color: "#3f97e0",
				opacity: 0.3,
			},
			stroke: {
				style: "solid",
				color: "#3f97e0",
				width: 4,
				opacity: 1,
			},
		},
	};

	if (geometryType === GEOMETRY_TYPE.POLYGON) {
		const polygonStyles = getPolygonSimpleStyles(polygonConfig);

		polygonStyles.forEach((style) => {
			const layerConfig = {
				...style,
				id: `selected-features-${layerId}-${style.id}`,
				source: sourceId,
				"source-layer": sourceLayer,
				filter: ["in", ["get", "gid"], ["literal", gids]],
			};

			mapInstance.addLayer(layerConfig as any);
		});
	}

	if (geometryType === GEOMETRY_TYPE.LINE) {
		const lineConfig = {
			symbolStyle: {
				fill: {
					style: "solid",
					color: "#3f97e0",
					width: 4,
					opacity: 1,
				},
			},
		};
		const lineStyles = getLineSimpleStyles(lineConfig);

		lineStyles.forEach((style) => {
			const layerConfig = {
				...style,
				id: `selected-features-${layerId}-${style.id}`,
				source: sourceId,
				"source-layer": sourceLayer,
				filter: ["in", ["get", "gid"], ["literal", gids]],
			};

			mapInstance.addLayer(layerConfig as any);
		});
	}

	if (geometryType === GEOMETRY_TYPE.POINT) {
		const pointConfig = {
			symbolStyle: {
				isGraphic: false,
				basic: {
					symbol: "circle",
					size: 6,
				},
				fill: {
					color: "#3f97e0",
					opacity: 0.3,
				},
				stroke: {
					color: "#3f97e0",
					width: 4,
					opacity: 1,
				},
			},
		};
		const pointStyles = getPointSimpleStyles(pointConfig);

		pointStyles.forEach((style) => {
			const layerConfig = {
				...style,
				id: `selected-features-${layerId}-${style.id}`,
				source: sourceId,
				"source-layer": sourceLayer,
				filter: ["in", ["get", "gid"], ["literal", gids]],
			};

			mapInstance.addLayer(layerConfig as any);
		});
	}
};

const clearSelectedFeatures = (map: MapRef) => {
	const mapInstance = map.getMap();

	// Find all query result layers dynamically
	const allLayers = mapInstance.getStyle().layers ?? [];
	const queryResultLayers = allLayers.filter((layer) =>
		layer.id.startsWith(`selected-features-`),
	);

	// Remove all query result layers
	queryResultLayers.forEach((layer) => {
		if (mapInstance.getLayer(layer.id)) {
			mapInstance.removeLayer(layer.id);
		}
	});
};

const addHighlightedFeatureToMap = (
	map: MapRef,
	gid: number,
	sourceId: string,
	sourceLayer: string,
	layerId: string,
	geometryType: GEOMETRY_TYPE,
	highlightColor: string,
) => {
	clearHighlightedFeature(map);

	const mapInstance = map.getMap();

	const highlightConfig = {
		symbolStyle: {
			fill: {
				style: "solid",
				color: highlightColor,
				opacity: 1.0, // 100% opacity for highlighting
			},
			stroke: {
				style: "solid",
				color: highlightColor,
				width: 5,
				opacity: 1,
			},
		},
	};

	if (geometryType === GEOMETRY_TYPE.POLYGON) {
		const polygonStyles = getPolygonSimpleStyles(highlightConfig);

		polygonStyles.forEach((style) => {
			const layerConfig = {
				...style,
				id: `highlighted-feature-${layerId}-${style.id}`,
				source: sourceId,
				"source-layer": sourceLayer,
				filter: ["==", ["get", "gid"], gid],
			};

			mapInstance.addLayer(layerConfig as any);
		});
	}

	if (geometryType === GEOMETRY_TYPE.LINE) {
		const lineConfig = {
			symbolStyle: {
				fill: {
					style: "solid",
					color: highlightColor,
					width: 5,
					opacity: 1,
				},
			},
		};
		const lineStyles = getLineSimpleStyles(lineConfig);

		lineStyles.forEach((style) => {
			const layerConfig = {
				...style,
				id: `highlighted-feature-${layerId}-${style.id}`,
				source: sourceId,
				"source-layer": sourceLayer,
				filter: ["==", ["get", "gid"], gid],
			};

			mapInstance.addLayer(layerConfig as any);
		});
	}

	if (geometryType === GEOMETRY_TYPE.POINT) {
		const pointConfig = {
			symbolStyle: {
				isGraphic: false,
				basic: {
					symbol: "circle",
					size: 8, // Slightly larger for highlight
				},
				fill: {
					color: highlightColor,
					opacity: 1.0, // 100% opacity for highlighting
				},
				stroke: {
					color: highlightColor,
					width: 5,
					opacity: 1,
				},
			},
		};
		const pointStyles = getPointSimpleStyles(pointConfig);

		pointStyles.forEach((style) => {
			const layerConfig = {
				...style,
				id: `highlighted-feature-${layerId}-${style.id}`,
				source: sourceId,
				"source-layer": sourceLayer,
				filter: ["==", ["get", "gid"], gid],
			};

			mapInstance.addLayer(layerConfig as any);
		});
	}
};

const clearHighlightedFeature = (map: MapRef) => {
	const mapInstance = map.getMap();

	// Find all highlighted feature layers dynamically
	const allLayers = mapInstance.getStyle().layers ?? [];
	const highlightedLayers = allLayers.filter((layer) =>
		layer.id.startsWith(`highlighted-feature-`),
	);

	// Remove all highlighted feature layers
	highlightedLayers.forEach((layer) => {
		if (mapInstance.getLayer(layer.id)) {
			mapInstance.removeLayer(layer.id);
		}
	});
};

const addQueryResultsToMap = (
	map: MapRef,
	gids: number[],
	highlightColor: string,
	sourceId: string,
	sourceLayer: string,
	layerId: string,
	geometryType: GEOMETRY_TYPE,
) => {
	clearQueryResults(map);

	const mapInstance = map.getMap();

	const polygonConfig = {
		symbolStyle: {
			fill: {
				style: "solid",
				color: highlightColor,
				opacity: 0.3,
			},
			stroke: {
				style: "solid",
				color: highlightColor,
				width: 4,
				opacity: 1,
			},
		},
	};

	if (geometryType === GEOMETRY_TYPE.POLYGON) {
		const polygonStyles = getPolygonSimpleStyles(polygonConfig);

		polygonStyles.forEach((style) => {
			const layerConfig = {
				...style,
				id: `query-results-${layerId}-${style.id}`,
				source: sourceId,
				"source-layer": sourceLayer,
				filter: ["in", ["get", "gid"], ["literal", gids]],
			};

			mapInstance.addLayer(layerConfig as any);
		});
	}

	if (geometryType === GEOMETRY_TYPE.LINE) {
		const lineConfig = {
			symbolStyle: {
				fill: {
					style: "solid",
					color: highlightColor,
					width: 4,
					opacity: 1,
				},
			},
		};
		const lineStyles = getLineSimpleStyles(lineConfig);

		lineStyles.forEach((style) => {
			const layerConfig = {
				...style,
				id: `query-results-${layerId}-${style.id}`,
				source: sourceId,
				"source-layer": sourceLayer,
				filter: ["in", ["get", "gid"], ["literal", gids]],
			};

			mapInstance.addLayer(layerConfig as any);
		});
	}

	if (geometryType === GEOMETRY_TYPE.POINT) {
		const pointConfig = {
			symbolStyle: {
				isGraphic: false,
				basic: {
					symbol: "circle",
					size: 6,
				},
				fill: {
					color: highlightColor,
					opacity: 0.3,
				},
				stroke: {
					color: highlightColor,
					width: 4,
					opacity: 1,
				},
			},
		};
		const pointStyles = getPointSimpleStyles(pointConfig);

		pointStyles.forEach((style) => {
			const layerConfig = {
				...style,
				id: `query-results-${layerId}-${style.id}`,
				source: sourceId,
				"source-layer": sourceLayer,
				filter: ["in", ["get", "gid"], ["literal", gids]],
			};

			mapInstance.addLayer(layerConfig as any);
		});
	}
};

const clearQueryResults = (map: MapRef) => {
	const mapInstance = map.getMap();

	// Find all query result layers dynamically
	const allLayers = mapInstance.getStyle().layers ?? [];
	const queryResultLayers = allLayers.filter((layer) =>
		layer.id.startsWith(`query-results-`),
	);

	// Remove all query result layers
	queryResultLayers.forEach((layer) => {
		if (mapInstance.getLayer(layer.id)) {
			mapInstance.removeLayer(layer.id);
		}
	});
};

const FIELD_TYPE_MAP: Record<ATTRIBUTE_FILTER_TYPE, QueryField["type"]> = {
	[ATTRIBUTE_FILTER_TYPE.TEXT_SEARCH]: "text",
	[ATTRIBUTE_FILTER_TYPE.NUMERIC_RANGE]: "numeric",
	[ATTRIBUTE_FILTER_TYPE.DATE_RANGE]: "date",
	[ATTRIBUTE_FILTER_TYPE.BOOLEAN]: "text",
	[ATTRIBUTE_FILTER_TYPE.ENUM_SELECTION]: "text",
};

const DATA_TYPE_MAP: Record<ATTRIBUTE_FILTER_TYPE, QueryField["dataType"]> = {
	[ATTRIBUTE_FILTER_TYPE.TEXT_SEARCH]: "categorical",
	[ATTRIBUTE_FILTER_TYPE.NUMERIC_RANGE]: "quantitative",
	[ATTRIBUTE_FILTER_TYPE.DATE_RANGE]: "temporal",
	[ATTRIBUTE_FILTER_TYPE.BOOLEAN]: "categorical",
	[ATTRIBUTE_FILTER_TYPE.ENUM_SELECTION]: "categorical",
};

const SPATIAL_TYPE_TO_DRAW_MODE_MAP: Record<
	SPATIAL_FILTER_TYPE,
	"point" | "circle" | "rectangle" | "polygon" | "select"
> = {
	[SPATIAL_FILTER_TYPE.POINT]: "point",
	[SPATIAL_FILTER_TYPE.CIRCLE]: "circle",
	[SPATIAL_FILTER_TYPE.RECTANGLE]: "rectangle",
	[SPATIAL_FILTER_TYPE.POLYGON]: "polygon",
	[SPATIAL_FILTER_TYPE.ALL]: "select",
};
